import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiError, api, fetchApi } from '@/lib/api';
import type { ItemsSearchParams } from '@/routes/items/-search-params';

function cleanParams(params: Omit<ItemsSearchParams, 'page'>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(params)) {
    if (key === 'page') continue;
    if (value === null || value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    cleaned[key] = value;
  }

  return cleaned;
}

/** Shared query options — used by both the hook and the loader prefetch.
 *  Guarantees cache key alignment between server prefetch and client hook. */
export function itemsInfiniteQueryOptions(
  params: Omit<ItemsSearchParams, 'page'>,
  initialPage = 1,
) {
  const cleanedParams = cleanParams(params);
  return {
    queryKey: ['items', 'search', cleanedParams] as const,
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const body = { ...cleanedParams, page: pageParam };
      const result = await fetchApi(api.api.items.search.post(body));
      return { ...result, page: pageParam };
    },
    initialPageParam: initialPage,
    getNextPageParam: (lastPage: { hasNextPage: boolean; page: number }) =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage: { page: number }) =>
      firstPage.page > 1 ? firstPage.page - 1 : undefined,
  };
}

export function useInfiniteItemsQuery(
  params: Omit<ItemsSearchParams, 'page'>,
  initialPage = 1,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;

  return useInfiniteQuery({
    ...itemsInfiniteQueryOptions(params, initialPage),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
